import { useState, useEffect } from 'react';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import { fetchFileContent } from '../shared';
import { ApiCatalog, ApiCategory } from './types';

interface UseApiCatalogResult {
  categories: ApiCategory[];
  restrictedCategories: ApiCategory[];
  loading: boolean;
  error: string | null;
}

export function useApiCatalog(
  owner: string,
  repo: string,
  path: string,
  ref?: string,
  restrictedPath?: string,
): UseApiCatalogResult {
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [restrictedCategories, setRestrictedCategories] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const githubAuth = useApi(githubAuthApiRef);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await githubAuth.getAccessToken(['repo']);

        // Fetch unrestricted APIs
        const text = await fetchFileContent(token, owner, repo, path, ref);
        const parsed = yaml.load(text) as ApiCatalog;
        if (parsed?.categories) {
          setCategories(parsed.categories);
        }

        // Fetch restricted APIs (non-critical — continue if it fails)
        if (restrictedPath) {
          try {
            const restrictedText = await fetchFileContent(token, owner, repo, restrictedPath, ref);
            const restrictedParsed = yaml.load(restrictedText) as ApiCatalog;
            if (restrictedParsed?.categories) {
              setRestrictedCategories(restrictedParsed.categories);
            }
          } catch {
            // Restricted catalog is optional — silently ignore
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load API catalog');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [githubAuth, owner, repo, path, ref, restrictedPath]);

  return { categories, restrictedCategories, loading, error };
}
